import React from 'react'
import { useTranslation } from 'react-i18next'
import { Gift, Lock, Medal, Ticket } from 'lucide-react'
import { formatPrice } from '../../../../utils/formatters'
import { CARD_CLASS, CrystalAmount, SectionTitle, WitchPortrait } from './shared'

// Оформление суперпризов по местам: золото, серебро, бронза
const PLACE_STYLES = {
  1: 'border-yellow-400/45 bg-gradient-to-b from-yellow-400/20 to-orange-500/5 text-yellow-200 shadow-[0_0_24px_rgba(250,204,21,0.15)]',
  2: 'border-slate-300/35 bg-gradient-to-b from-slate-300/15 to-transparent text-slate-200',
  3: 'border-amber-600/40 bg-gradient-to-b from-amber-600/20 to-transparent text-amber-300'
}

/**
 * Призы финального розыгрыша: суперпризы по местам, призы ведьмы
 * (с замком, пока ведьма не накормлена) и утешительные.
 * tickets — тыквы своей банды (= её билеты), null у гостя и игрока без банды.
 */
const PrizesCard = ({ prizes, tickets = null }) => {
  const { t } = useTranslation()
  if (!prizes) return null
  const superPrizes = [...(prizes.super || [])].sort((a, b) => a.place - b.place)
  const { witch, consolation } = prizes

  return (
    <section className={`${CARD_CLASS} p-5`}>
      <SectionTitle icon={<Gift size={18} className="text-yellow-300" />}>
        {t('halloweenEvent.prizes.title')}
      </SectionTitle>

      {tickets != null && (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-orange-400/30 bg-orange-500/10 px-2.5 py-1 text-xs font-bold text-orange-100">
          <Ticket size={13} className="text-orange-300" />
          {t('halloweenEvent.prizes.tickets', { count: tickets })}
        </div>
      )}

      {/* Суперпризы: 1 место — по центру и крупнее */}
      {superPrizes.length > 0 && (
        <div className="mt-4 grid grid-cols-3 items-end gap-2">
          {[superPrizes[1], superPrizes[0], superPrizes[2]].filter(Boolean).map((prize) => (
            <div
              key={prize.place}
              className={`flex flex-col items-center rounded-2xl border px-1.5 text-center ${PLACE_STYLES[prize.place] || PLACE_STYLES[3]} ${prize.place === 1 ? 'pb-4 pt-5' : 'pb-3 pt-3.5'}`}
            >
              <Medal size={prize.place === 1 ? 28 : 22} />
              <p className="mt-1 text-[11px] font-bold uppercase text-white/80">
                {t('halloweenEvent.prizes.place', { place: prize.place })}
              </p>
              <CrystalAmount
                value={prize.amount}
                iconSize={prize.place === 1 ? 14 : 12}
                className={`mt-1 justify-center font-black text-white ${prize.place === 1 ? 'text-base' : 'text-xs'}`}
              />
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
        {witch?.count > 0 && (
          <div className={`relative flex items-center gap-3 overflow-hidden rounded-xl border px-3 py-3 ${witch.locked ? 'border-white/10 bg-black/30' : 'border-lime-400/30 bg-lime-400/[0.07]'}`}>
            <div className={witch.locked ? 'opacity-50 grayscale' : ''}>
              <WitchPortrait size={40} fed={!witch.locked} />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`text-[11px] font-bold uppercase tracking-wide ${witch.locked ? 'text-text-secondary' : 'text-lime-300'}`}>
                {t('halloweenEvent.prizes.witch')}
              </p>
              <p className="mt-0.5 text-sm font-black tabular-nums text-white">
                {witch.count} × {formatPrice(witch.amount)} 💎
              </p>
              {witch.locked && (
                <p className="mt-0.5 text-[11px] leading-snug text-text-secondary">{t('halloweenEvent.prizes.witchLocked')}</p>
              )}
            </div>
            {witch.locked && <Lock size={18} className="shrink-0 text-text-secondary" />}
          </div>
        )}

        {consolation?.count > 0 && (
          <div className="flex items-center gap-3 rounded-xl border border-orange-400/20 bg-orange-500/[0.06] px-3 py-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-orange-400/30 bg-orange-500/15">
              <Gift size={18} className="text-orange-200" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wide text-orange-300">
                {t('halloweenEvent.prizes.consolation')}
              </p>
              <p className="mt-0.5 text-sm font-black tabular-nums text-white">
                {consolation.count} × {formatPrice(consolation.amount)} 💎
              </p>
            </div>
          </div>
        )}
      </div>

      <p className="mt-3 text-xs leading-relaxed text-text-secondary">{t('halloweenEvent.prizes.note')}</p>
    </section>
  )
}

export default PrizesCard
